import React, { useState } from 'react';
import styled from 'styled-components';

import { Button } from '@monolith/components/0-atoms/Button';
import Icon from '@monolith/components/0-atoms/Icon';
import { Typography } from '@opus1/harmony';
import {
    STAFF_IMPORT_PAGE_COPY,
    STAFF_IMPORT_TEMPLATE_URL,
} from '../constants/staffImport';
import { staffImportTokens } from '../styles/staffImportTokens';
import { StaffImportCsvUploadField } from './StaffImportCsvUploadField';
import { StaffImportFieldRequirementsModal } from './StaffImportFieldRequirementsModal';
import { StaffImportOrDivider } from './StaffImportOrDivider';
import { StaffImportSectionDivider } from './StaffImportSectionDivider';
import { StaffImportWebCrawlField } from './StaffImportWebCrawlField';

export interface StaffImportFormProps {
    businessDisplayName: string;
    selectedFile: File | null;
    uploadedFileName: string | null;
    csvInlineError: string | null;
    webCrawlInlineError: string | null;
    csvUploadLoading: boolean;
    csvDisabled: boolean;
    webCrawlLoading: boolean;
    webCrawlDisabled: boolean;
    staffPageUrl?: string | null;
    hasExistingImport: boolean;
    onFileSelect: (file: File) => void;
    onUpload: () => void;
    onWebCrawl: (url: string) => void;
    onWebCrawlUrlChange?: () => void;
}

const FormSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${staffImportTokens.spacing.sectionGap}px;
    width: 100%;
`;

const HeaderSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${staffImportTokens.spacing.sectionGap}px;
`;

const BusinessContext = styled.p`
    margin: 0;
    font-family: 'Montserrat', sans-serif;
    font-size: ${staffImportTokens.typography.bodyLarge.fontSize};
    font-weight: ${staffImportTokens.typography.bodyLarge.fontWeight};
    line-height: ${staffImportTokens.typography.bodyLarge.lineHeight};
    color: ${staffImportTokens.colors.textPrimary};
`;

const BusinessName = styled.span`
    font-weight: 700;
`;

const IntroCopy = styled.p`
    margin: 0;
    max-width: 677px;
    font-family: 'Montserrat', sans-serif;
    font-size: ${staffImportTokens.typography.bodyLarge.fontSize};
    font-weight: ${staffImportTokens.typography.bodyLarge.fontWeight};
    line-height: ${staffImportTokens.typography.bodyLarge.lineHeight};
    color: ${staffImportTokens.colors.textPrimary};
`;

const FieldRequirementsButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: ${staffImportTokens.spacing.sectionGap}px;
    width: fit-content;
    padding: 0;
    border: none;
    background: none;
    font-family: 'Montserrat', sans-serif;
    font-size: ${staffImportTokens.typography.bodyLarge.fontSize};
    font-weight: ${staffImportTokens.typography.bodyLarge.fontWeight};
    line-height: ${staffImportTokens.typography.bodyLarge.lineHeight};
    color: ${staffImportTokens.colors.linkPrimary};
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
`;

const DownloadTemplateButton = styled(Button)`
    width: fit-content;
    align-self: flex-start;
    gap: 8px;
`;

export const StaffImportForm: React.FC<StaffImportFormProps> = ({
    businessDisplayName,
    selectedFile,
    uploadedFileName,
    csvInlineError,
    webCrawlInlineError,
    csvUploadLoading,
    csvDisabled,
    webCrawlLoading,
    webCrawlDisabled,
    staffPageUrl,
    hasExistingImport,
    onFileSelect,
    onUpload,
    onWebCrawl,
    onWebCrawlUrlChange,
}) => {
    const [showFieldRequirementsModal, setShowFieldRequirementsModal] =
        useState(false);

    return (
        <FormSection>
            <HeaderSection>
                <Typography variant="title" weight={700} as="h1">
                    {STAFF_IMPORT_PAGE_COPY.title}
                </Typography>
                <BusinessContext>
                    You are about to import staff for{' '}
                    <BusinessName>{businessDisplayName}</BusinessName>
                </BusinessContext>
                <IntroCopy>{STAFF_IMPORT_PAGE_COPY.intro}</IntroCopy>
                <FieldRequirementsButton
                    type="button"
                    id="staff-import-field-requirements"
                    onClick={() => setShowFieldRequirementsModal(true)}
                >
                    {STAFF_IMPORT_PAGE_COPY.fieldRequirementsLabel}
                    <Icon fasIcon="arrow-up-right-from-square" />
                </FieldRequirementsButton>
                <DownloadTemplateButton
                    id="staff-import-download-template"
                    variant="secondary"
                    size="large"
                    href={STAFF_IMPORT_TEMPLATE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <Icon farIcon="download" />
                    {STAFF_IMPORT_PAGE_COPY.downloadTemplateLabel}
                </DownloadTemplateButton>
            </HeaderSection>

            <StaffImportSectionDivider />

            <StaffImportCsvUploadField
                id="staff-import-csv-upload"
                selectedFile={selectedFile}
                uploadedFileName={uploadedFileName}
                uploading={csvUploadLoading}
                disabled={csvDisabled}
                errorMessage={csvInlineError || undefined}
                hasExistingImport={hasExistingImport}
                onFileSelect={onFileSelect}
                onUpload={onUpload}
            />

            <StaffImportOrDivider />

            <StaffImportWebCrawlField
                id="staff-import-web-crawl-url"
                disabled={webCrawlDisabled}
                loading={webCrawlLoading}
                csvUploadLoading={csvUploadLoading}
                hasExistingImport={hasExistingImport}
                staffPageUrl={staffPageUrl}
                errorMessage={webCrawlInlineError || undefined}
                onWebCrawl={onWebCrawl}
                onUrlChange={onWebCrawlUrlChange}
            />

            <StaffImportSectionDivider />

            <StaffImportFieldRequirementsModal
                isOpen={showFieldRequirementsModal}
                onClose={() => setShowFieldRequirementsModal(false)}
            />
        </FormSection>
    );
};

export default StaffImportForm;
